import { useEffect, useState } from "react";
import { proposeTrail, ApiError, type TrailDetail } from "../api";
import { MapKitPlacePicker } from "./MapKitPlacePicker";
import type { MapKitHit } from "../mapkit";
import { AmenityPickerGrid } from "./AmenityPickerGrid";
import { AMENITIES_HEADING } from "../amenities";

/**
 * Add-trail drawer — Phase 4.
 *
 * Same drawer chassis the edit flow uses, but scoped to creating a
 * brand-new trail via /api/v1/admin/trails (proposeTrail). Only
 * Identity + Drop a Pin sections render — there's no Crew / Status
 * yet since the trail doesn't exist until Save. On success the
 * parent workspace receives the freshly-minted TrailDetail and decides
 * whether to immediately open the edit drawer on it (typical UX:
 * "yes, keep going").
 *
 * If the worker dedupes the proposal (matched name + state-page-url
 * already in the catalog) the new trail is the existing one and the
 * deduped flag is true; we surface a small note so the curator
 * understands they didn't create a new row.
 */

export function AddTrailDrawer({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  /** Fired after a successful create. `deduped` lets the parent UI
   *  explain "this already existed" when the worker matched an
   *  existing row instead of inserting. */
  onCreated: (trail: TrailDetail, deduped: boolean) => void;
}) {
  const [name, setName] = useState("");
  const [statusPageUrl, setStatusPageUrl] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [region, setRegion] = useState("");
  const [stateCode, setStateCode] = useState("");
  const [country] = useState("US");
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  /** Amenities the curator has pre-checked on the new trail. The
   *  worker writes each as a `amenity.<key>` row in trail_attributes
   *  at create time. */
  const [amenities, setAmenities] = useState<Set<string>>(new Set());

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") requestClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const canSave =
    name.trim().length > 0 && statusPageUrl.trim().length > 0 && !saving;

  function requestClose() {
    const dirty =
      name || statusPageUrl || websiteUrl || region || stateCode ||
      latitude != null || longitude != null ||
      amenities.size > 0;
    if (
      dirty &&
      !confirm("Bail out without saving this new trail?")
    ) {
      return;
    }
    setOpen(false);
    setTimeout(onClose, 200);
  }

  async function handleSave() {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const result = await proposeTrail({
        name: name.trim(),
        statusPageUrl: statusPageUrl.trim(),
        websiteUrl: websiteUrl.trim() || undefined,
        region: region.trim() || undefined,
        state: stateCode.trim() || undefined,
        latitude: latitude ?? undefined,
        longitude: longitude ?? undefined,
        amenities: amenities.size > 0 ? Array.from(amenities) : undefined,
      });
      // Trigger the slide-out before unmounting so the new drawer can
      // animate in cleanly.
      setOpen(false);
      setTimeout(() => onCreated(result.trail, result.deduped), 200);
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't save. Try again?"
      );
    } finally {
      setSaving(false);
    }
  }

  function applyPickedLocation(hit: MapKitHit) {
    setLatitude(hit.latitude);
    setLongitude(hit.longitude);
    // Only backfill empty fields — same rule the edit drawer uses.
    if (!region.trim() && hit.city) setRegion(hit.city);
    if (!stateCode.trim() && hit.stateCode) setStateCode(hit.stateCode);
  }

  return (
    <div
      className={`drawer-backdrop ${open ? "open" : ""}`}
      onClick={requestClose}
      aria-hidden={!open}
    >
      <aside
        className={`drawer ${open ? "open" : ""}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Add a trail"
      >
        <header className="drawer-head">
          <div>
            <div className="drawer-eyebrow">New trail</div>
            <h2 className="drawer-title">
              {name.trim() || "Untitled trail"}
            </h2>
            <div className="drawer-sub">
              Fill in the basics, drop a pin, then save.
            </div>
          </div>
          <button
            type="button"
            className="secondary small"
            onClick={requestClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="drawer-body">
          <section className="drawer-section">
            <header>
              <h3>The basics</h3>
              <p className="muted">Name + the page where status lives.</p>
            </header>
            <div className="drawer-section-body">
              <label className="drawer-field">
                <span className="drawer-field-label">Trail name *</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  placeholder="e.g. Blankets Creek"
                />
              </label>
              <label className="drawer-field">
                <span className="drawer-field-label">Status page URL *</span>
                <input
                  type="url"
                  value={statusPageUrl}
                  onChange={(e) => setStatusPageUrl(e.target.value)}
                  placeholder="https://"
                />
                <span className="drawer-field-hint">
                  Where the worker fetches the open/closed signal.
                </span>
              </label>
              <div className="drawer-row">
                <label className="drawer-field">
                  <span className="drawer-field-label">City / region</span>
                  <input
                    type="text"
                    value={region}
                    placeholder="e.g. Woodstock"
                    onChange={(e) => setRegion(e.target.value)}
                  />
                </label>
                <label className="drawer-field">
                  <span className="drawer-field-label">State</span>
                  <input
                    type="text"
                    value={stateCode}
                    placeholder="GA"
                    maxLength={4}
                    onChange={(e) =>
                      setStateCode(e.target.value.toUpperCase())
                    }
                  />
                </label>
                <label className="drawer-field">
                  <span className="drawer-field-label">Country</span>
                  <input type="text" value={country} disabled />
                </label>
              </div>
              <label className="drawer-field">
                <span className="drawer-field-label">Website (optional)</span>
                <input
                  type="url"
                  value={websiteUrl}
                  placeholder="https://"
                  onChange={(e) => setWebsiteUrl(e.target.value)}
                />
              </label>
            </div>
          </section>

          <section className="drawer-section">
            <header>
              <h3>Drop a pin</h3>
              <p className="muted">
                Optional — but it's how riders find the trail on the map.
              </p>
            </header>
            <div className="drawer-section-body">
              <div className="drawer-coords">
                <span className="muted">Current pin</span>
                <code>
                  {latitude != null && longitude != null
                    ? `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
                    : "Not set yet"}
                </code>
              </div>
              <MapKitPlacePicker
                embedded
                initialQuery={name}
                nearCity={region}
                nearState={stateCode}
                initialLatitude={latitude}
                initialLongitude={longitude}
                onPick={applyPickedLocation}
              />
            </div>
          </section>

          <section className="drawer-section">
            <header>
              <h3>{AMENITIES_HEADING}</h3>
              <p className="muted">
                Optional — tap any amenity the trail has at the
                trailhead. Saved with the new trail so riders see them
                from day one.
              </p>
            </header>
            <div className="drawer-section-body">
              <AmenityPickerGrid
                selected={amenities}
                onToggle={(key) => {
                  setAmenities((prev) => {
                    const next = new Set(prev);
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }}
              />
            </div>
          </section>
        </div>

        <footer className="drawer-foot">
          <div className="drawer-foot-actions">
            {/* Spacer left side — no Delete affordance on a new trail
                that doesn't exist yet. Keeps Save / Bail aligned right
                so the visual rhythm matches the edit drawer. */}
            <span />
            <div className="row" style={{ gap: 8 }}>
              <button
                type="button"
                className="secondary"
                onClick={requestClose}
                disabled={saving}
              >
                Bail out
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!canSave}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
          <div className="drawer-foot-status">
            {saving ? (
              <span className="muted">Saving…</span>
            ) : error ? (
              <span className="error">{error}</span>
            ) : (
              <span className="muted">
                {name.trim() && statusPageUrl.trim()
                  ? "Ready to save"
                  : "Name + status URL required"}
              </span>
            )}
          </div>
        </footer>
      </aside>
    </div>
  );
}
